import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TablePaginationProps {
  page: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
  className?: string
}

function getVisiblePages(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i)

  const near = new Set([0, 1, current - 1, current, current + 1, total - 2, total - 1])
  const sorted = [...near].filter(p => p >= 0 && p < total).sort((a, b) => a - b)
  const pages: (number | 'ellipsis')[] = []
  let prev = -1

  for (const p of sorted) {
    if (prev !== -1 && p - prev > 1) pages.push('ellipsis')
    pages.push(p)
    prev = p
  }

  return pages
}

export function TablePagination({ page, totalItems, pageSize, onPageChange, className }: TablePaginationProps) {
  const totalPages = Math.ceil(totalItems / pageSize)
  if (totalPages <= 1) return null

  const safePage = Math.min(page, totalPages - 1)
  const start = safePage * pageSize + 1
  const end = Math.min((safePage + 1) * pageSize, totalItems)
  const visiblePages = getVisiblePages(safePage, totalPages)

  return (
    <div className={cn('flex items-center justify-between border-t border-border px-4 py-3', className)}>
      <span className="text-sm text-muted-foreground">
        {start}-{end} of {totalItems}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage === 0}
          className="rounded-lg border border-border px-2 py-1.5 text-sm disabled:opacity-50 hover:bg-muted"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {visiblePages.map((entry, i) =>
          entry === 'ellipsis' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-sm text-muted-foreground">...</span>
          ) : (
            <button
              key={entry}
              onClick={() => onPageChange(entry)}
              className={cn(
                'rounded-lg border px-2.5 py-1.5 text-sm',
                entry === safePage
                  ? 'bg-brand-fg/20 text-brand-fg border-brand-fg/30'
                  : 'border-border hover:bg-muted text-muted-foreground',
              )}
            >
              {entry + 1}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= totalPages - 1}
          className="rounded-lg border border-border px-2 py-1.5 text-sm disabled:opacity-50 hover:bg-muted"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
